/**
 * Summarise a farm-level batch of recache requests into one snackbar message.
 *
 * Shared between the old /mapping UI (mapping.component.ts) and the new /maps UI
 * (maps-landing.component.ts) so both report the exact same three outcomes the
 * same way, rather than drifting into two different wordings for the same thing.
 *
 * Three outcomes, not two: a request can be newly queued, already in flight (the
 * backend's own dedupe refusing a duplicate for the exact same analysisId -- see
 * recacheManagement.js's isAlreadyInFlight), or genuinely failed. Folding
 * "already in flight" into either of the other two would either hide that the
 * dedupe did something, or wrongly read as a problem.
 */
export type RecacheOutcome = 'queued' | 'inProgress' | 'failed';

export function summariseFarmRecacheResults(results: RecacheOutcome[]): string {
  const queued = results.filter(r => r === 'queued').length;
  const inProgress = results.filter(r => r === 'inProgress').length;
  const failed = results.filter(r => r === 'failed').length;
  const total = results.length;

  // Every one of them was already in flight -- most likely a repeat click on the same request.
  // Lead with that plainly, in the same voice as the single-field 409 message, rather than a bare
  // count of fields.
  if (queued === 0 && failed === 0 && inProgress === total) {
    return total === 1
      ? 'This field is already recaching — please wait a few minutes before trying again.'
      : `These ${total} fields are already recaching — please wait a few minutes before trying again.`;
  }

  // Mixed result: some were already in flight, say so first, then whatever was actually new.
  const parts: string[] = [];
  if (inProgress > 0) { parts.push(`${inProgress} field${inProgress === 1 ? '' : 's'} already recaching`); }
  if (queued > 0) { parts.push(`${queued} field${queued === 1 ? '' : 's'} queued`); }
  if (failed > 0) { parts.push(`${failed} could not be queued`); }

  return `${parts.join(', ')} — this can take a few minutes.`;
}
